import moment from "moment";

import {
    api_getAppointmentDraft,
    AppointmentDraft,
} from "../redux/appointmentDraft/appointmentDraft.api";
import type {AppDispatch} from "../redux/store";
import {
    updateIsImmediate,
    updatePickedDate,
    updatePickedTimeSlot,
    updateRequestReason,
    updateSelectedState,
    updateServiceType,
    updateVetId,
    updateVetProfile,
    updateVetProfileId,
} from "../redux/scheduleAppointment/scheduling.actions";
import {setPrice} from "../redux/scheduleAppointment/scheduling.reducers";
import {setAppointmentPrice} from "../Constants/serviceType";

export const GUEST_BOOKING_STORAGE_KEY = "guestBooking";

export const DEFAULT_GUEST_SERVICE_TYPE = "RX_VIDEO";
export const DEFAULT_GUEST_REASON_TYPE = "PRESCRIPTION";
export const GUEST_RX_APPOINTMENT_PRICE = "75";

export type GuestBookingState = {
    petType: string;
    specialtyFilter: string;
    vetProfileId: number;
    vetId: number;
    vetProfile: object;
    pickedDate: string;
    pickedTimeSlot: string[];
    serviceType: string;
    reasonType: string;
    state: string;
    step?: "pet_select" | "complete";
};

export type GuestBookingIntentParams = {
    petType: string;
    vetProfileId: number;
    vetId: number;
    state: string;
    pickedTimeSlot: string;
};

const readStorage = (): string | null => {
    return (
        localStorage.getItem(GUEST_BOOKING_STORAGE_KEY) ||
        sessionStorage.getItem(GUEST_BOOKING_STORAGE_KEY)
    );
};

const writeStorage = (raw: string) => {
    localStorage.setItem(GUEST_BOOKING_STORAGE_KEY, raw);
    sessionStorage.setItem(GUEST_BOOKING_STORAGE_KEY, raw);
};

export const buildGuestBookingState = (
    intent: GuestBookingIntentParams,
    partial?: Partial<GuestBookingState>
): GuestBookingState => {
    const slot = intent.pickedTimeSlot;
    return {
        petType: intent.petType,
        specialtyFilter: intent.petType,
        vetProfileId: intent.vetProfileId,
        vetId: intent.vetId,
        vetProfile: partial?.vetProfile || {},
        pickedDate: moment(slot).toISOString(),
        pickedTimeSlot: [slot],
        serviceType: DEFAULT_GUEST_SERVICE_TYPE,
        reasonType: DEFAULT_GUEST_REASON_TYPE,
        state: intent.state,
        step: "pet_select",
        ...partial,
    };
};

export const mapAppointmentDraftToGuestBooking = (draft: AppointmentDraft): GuestBookingState => {
    return buildGuestBookingState({
        petType: draft.animalType,
        vetProfileId: draft.vetProfileId,
        vetId: draft.vetUserId,
        pickedTimeSlot: draft.pickedTimeSlot,
        state: draft.state,
    });
};

export const getAppointmentDraftPayload = (clientEmail: string): AppointmentDraft | null => {
    const booking = getGuestBooking();
    if (!booking?.vetProfileId || !booking.pickedTimeSlot?.length) {
        return null;
    }
    return {
        clientEmail: clientEmail.toLowerCase(),
        vetUserId: booking.vetId,
        vetProfileId: booking.vetProfileId,
        animalType: booking.petType,
        state: booking.state || "",
        pickedTimeSlot: booking.pickedTimeSlot[0],
    };
};

export const resolveAppointmentDraftDefaults = async (): Promise<{
    state?: string;
    animalType?: string;
}> => {
    const guestBooking = getGuestBooking();

    try {
        const draft = await api_getAppointmentDraft();
        return {
            state: draft?.state || guestBooking?.state,
            animalType: draft?.animalType || guestBooking?.petType,
        };
    } catch {
        return {
            state: guestBooking?.state,
            animalType: guestBooking?.petType,
        };
    }
};

export const fetchAndApplyAppointmentDraft = async (): Promise<GuestBookingState | null> => {
    try {
        const draft = await api_getAppointmentDraft();
        if (!draft?.vetProfileId || !draft?.pickedTimeSlot || !draft?.animalType) {
            return null;
        }
        const booking = mapAppointmentDraftToGuestBooking(draft);
        setGuestBooking(booking);
        return booking;
    } catch {
        return null;
    }
};

export const getGuestBooking = (): GuestBookingState | null => {
    try {
        const raw = readStorage();
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

export const setGuestBooking = (state: GuestBookingState) => {
    writeStorage(JSON.stringify(state));
};

export const clearGuestBooking = () => {
    localStorage.removeItem(GUEST_BOOKING_STORAGE_KEY);
    sessionStorage.removeItem(GUEST_BOOKING_STORAGE_KEY);
};

export const hasGuestBookingPending = (): boolean => !!getGuestBooking();

export const getGuestBookingResumePath = (): string | null => {
    const booking = getGuestBooking();
    if (!booking?.vetProfileId) return null;
    return "/guest/book";
};

export const hasOnboardingCookie = (): boolean => {
    return /(?:^|;\s*)isOnBoarding\s*=\s*true/.test(document.cookie);
};

export const setOnboardingCookie = () => {
    const expiry = new Date();
    expiry.setTime(expiry.getTime() + 604800000);
    document.cookie = `isOnBoarding=true;path=/;expires=${expiry.toUTCString()}`;
};

export const updateGuestBooking = (partial: Partial<GuestBookingState>) => {
    const current = getGuestBooking();
    if (current) {
        setGuestBooking({...current, ...partial});
    }
};

export const getGuestBookingPrice = (serviceType: string = DEFAULT_GUEST_SERVICE_TYPE): string => {
    if (serviceType === DEFAULT_GUEST_SERVICE_TYPE) {
        return GUEST_RX_APPOINTMENT_PRICE;
    }
    return setAppointmentPrice(serviceType);
};

export const applyGuestBookingToSchedule = (dispatch: AppDispatch, booking: GuestBookingState) => {
    const serviceType = booking.serviceType || DEFAULT_GUEST_SERVICE_TYPE;

    dispatch(updateServiceType(serviceType));
    dispatch(updateRequestReason(booking.reasonType || DEFAULT_GUEST_REASON_TYPE));
    dispatch(updateIsImmediate(false));

    if (!booking.vetProfileId || !booking.pickedTimeSlot?.length) {
        return;
    }

    dispatch(updateVetProfileId(booking.vetProfileId));
    dispatch(updateVetProfile(booking.vetProfile));
    dispatch(updateVetId(booking.vetId));
    dispatch(updatePickedDate(booking.pickedDate));
    dispatch(updatePickedTimeSlot(booking.pickedTimeSlot));
    dispatch(updateSelectedState(booking.state));
    dispatch(setPrice(getGuestBookingPrice(serviceType)));
};

export const ensureSchedulePrice = (dispatch: AppDispatch, serviceType?: string, currentPrice?: string) => {
    if (currentPrice) {
        return currentPrice;
    }

    const price = getGuestBookingPrice(serviceType || DEFAULT_GUEST_SERVICE_TYPE);
    dispatch(setPrice(price));
    return price;
};
